import { createPath } from 'react-router';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { toCreateDataSourcesMetric, toCreateMetric, toCreateMetricGroup } from '@gonfalon/navigator/navigators';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import type { Path } from '@gonfalon/router/isPath';
import { TextButton } from '@gonfalon/ui/Button';
import { Button, Link, LinkButton, ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import styles from './MetricsSearch.module.css';

interface MetricsSearchEmptyStateProps {
  category: string;
  searchValue: string;
  projectKey: string;
  environmentKey: string;
  disableCreate?: boolean;
  expectedIntegrationKey?: string;
  onCategoryChange: (category: string) => void;
  onCreateMetric?: () => void;
  onCreateMetricGroup?: () => void;
}

// Helper function to render create button/link
const renderCreateAction = (
  text: string,
  path: Path,
  onPress?: () => void,
  disabled?: boolean,
  showExternalIcon = true,
) => {
  if (disabled) {
    return null;
  }

  if (onPress) {
    return <Button onPress={onPress}>{text}</Button>;
  }

  return (
    <LinkButton href={path} target="_blank">
      {text} {showExternalIcon && <Icon name="link-external" />}
    </LinkButton>
  );
};

function WarehouseNativeEmptyState({
  projectKey,
  environmentKey,
  expectedIntegrationKey,
}: {
  projectKey: string;
  environmentKey: string;
  expectedIntegrationKey?: string;
}) {
  const { data, isPending } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));

  if (isPending) {
    return (
      <div className={styles.emptyState} role="status">
        <ProgressBar isIndeterminate aria-label={msg('metrics.searchEmptyState.loadingDataSourcesLabel')} />
      </div>
    );
  }

  const matchingDataSources = (data?.items ?? []).filter((dataSource) => {
    if (dataSource.environmentKey !== environmentKey) {
      return false;
    }

    if (expectedIntegrationKey) {
      return dataSource.integrationKey === expectedIntegrationKey;
    }

    return true;
  });

  if (matchingDataSources.length === 0) {
    const createMetricDataSourcePath = expectedIntegrationKey
      ? toCreateDataSourcesMetric(
          { projectKey },
          {
            search: new URLSearchParams({
              integrationKey: expectedIntegrationKey,
              projectKey,
            }),
          },
        )
      : toCreateDataSourcesMetric({ projectKey });

    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.createMetricDataSource" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.noMetricDataSources" />
        </p>
        <LinkButton
          href={createPath(createMetricDataSourcePath)}
          target="_blank"
          onPress={() => {
            trackMetricsEvent('Clicked CTA Create Metric Data Source', { location: 'metricsSearch' });
          }}
        >
          {msg('metrics.searchEmptyState.createMetricDataSource')} <Icon name="link-external" />
        </LinkButton>
      </div>
    );
  }

  return (
    <div className={styles.emptyState}>
      <h3 className={styles.emptyStateTitle}>
        <Message id="metrics.searchEmptyState.noWarehouseMetricsHeading" />
      </h3>
      <p className={styles.emptyStateDescription}>
        <Message
          id="metrics.searchEmptyState.noWarehouseMetricsBody"
          values={{
            link: (chunks) => (
              <Link
                href={getDocumentationUrl('/home/warehouse-native/metrics')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </p>
      {renderCreateAction(
        msg('metrics.searchEmptyState.createMetric'),
        toCreateMetric({ projectKey, environmentKey }, { search: { dataSourceKey: matchingDataSources[0].key } }),
      )}
    </div>
  );
}

export function MetricsSearchEmptyState({
  category,
  searchValue,
  projectKey,
  environmentKey,
  disableCreate,
  expectedIntegrationKey,
  onCategoryChange,
  onCreateMetric,
  onCreateMetricGroup,
}: MetricsSearchEmptyStateProps) {
  const hasSearchQuery = searchValue && searchValue.trim().length > 0;

  // Currently selected category - no search logic needed
  if (category === 'currently-selected') {
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noneSelectedHeading" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.noneSelectedBody" />
        </p>
      </div>
    );
  }

  // Metric groups category
  if (category === 'metric-groups' || category === 'funnel-groups') {
    if (!hasSearchQuery) {
      // No search query - show create action
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>
            <Message id="metrics.searchEmptyState.noGroupsHeading" />
          </h3>
          <p className={styles.emptyStateDescription}>
            <Message id="metrics.searchEmptyState.noGroupsBody" />
          </p>
          {renderCreateAction(
            msg('metrics.searchEmptyState.createMetricGroup'),
            toCreateMetricGroup({ projectKey, environmentKey }),
            onCreateMetricGroup,
            disableCreate,
          )}
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noGroupsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            {msg('metrics.searchEmptyState.allMetrics')}
          </TextButton>
        </p>
      </div>
    );
  }

  // Autogenerated category
  if (category === 'autogenerated') {
    if (!hasSearchQuery) {
      // No search query - show autogenerated-specific empty state
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>
            <Message id="metrics.searchEmptyState.noAutogeneratedHeading" />
          </h3>
          <p className={styles.emptyStateDescription}>
            <Message
              id="metrics.searchEmptyState.noAutogeneratedBody"
              values={{
                link: (chunks) => (
                  <Link
                    href={getDocumentationUrl('/home/metrics/autogen-metrics')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </p>
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            {msg('metrics.searchEmptyState.allMetrics')}
          </TextButton>
        </p>
      </div>
    );
  }

  // AgentControl category
  if (category === 'agent-control') {
    if (!hasSearchQuery) {
      // No search query - show AgentControl-specific empty state
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>
            <Message id="metrics.searchEmptyState.noAgentControlHeading" />
          </h3>
          <p className={styles.emptyStateDescription}>
            <Message
              id="metrics.searchEmptyState.noAgentControlBody"
              values={{
                link: (chunks) => (
                  <Link
                    href={getDocumentationUrl('/home/metrics/autogen-metrics')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </p>
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            {msg('metrics.searchEmptyState.allMetrics')}
          </TextButton>
        </p>
      </div>
    );
  }

  // Warehouse native category
  if (category === 'warehouse-native') {
    if (!hasSearchQuery) {
      return (
        <WarehouseNativeEmptyState
          projectKey={projectKey}
          environmentKey={environmentKey}
          expectedIntegrationKey={expectedIntegrationKey}
        />
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            {msg('metrics.searchEmptyState.allMetrics')}
          </TextButton>
        </p>
      </div>
    );
  }

  // User-defined category
  if (category === 'user-defined') {
    if (!hasSearchQuery) {
      // No search query - show user-defined-specific empty state
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>
            <Message id="metrics.searchEmptyState.noUserDefinedHeading" />
          </h3>
          <p className={styles.emptyStateDescription}>
            <Message id="metrics.searchEmptyState.noMetricsBody" values={{ br: () => <br /> }} />
          </p>
          {renderCreateAction(
            msg('metrics.searchEmptyState.createMetric'),
            toCreateMetric({ projectKey, environmentKey }),
            onCreateMetric,
            disableCreate,
          )}
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            {msg('metrics.searchEmptyState.allMetrics')}
          </TextButton>
        </p>
      </div>
    );
  }

  // Release guardrails category
  if (category === 'release-guardrails') {
    if (!hasSearchQuery) {
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>
            <Message id="metrics.searchEmptyState.noGuardrailsHeading" />
          </h3>
          <p className={styles.emptyStateDescription}>
            <Message id="metrics.searchEmptyState.noGuardrailsBody" />
          </p>
        </div>
      );
    }

    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            {msg('metrics.searchEmptyState.allMetrics')}
          </TextButton>
        </p>
      </div>
    );
  }

  // All metrics category or other metric categories
  const isAllMetricsCategory = category === 'all';

  if (!hasSearchQuery) {
    // No search query - show default empty state
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsHeading" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.noMetricsBody" values={{ br: () => <br /> }} />
        </p>
        {renderCreateAction(
          msg('metrics.searchEmptyState.createMetric'),
          toCreateMetric({ projectKey, environmentKey }),
          onCreateMetric,
          disableCreate,
        )}
      </div>
    );
  }

  // Has search query
  if (isAllMetricsCategory) {
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>
          <Message id="metrics.searchEmptyState.noMetricsFound" />
        </h3>
        <p className={styles.emptyStateDescription}>
          <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('metric-groups')}>
            {msg('metrics.searchEmptyState.metricGroups')}
          </TextButton>
        </p>
      </div>
    );
  }

  // Other categories (most-used, recently-added) with search query
  return (
    <div className={styles.emptyState}>
      <h3 className={styles.emptyStateTitle}>
        <Message id="metrics.searchEmptyState.noMetricsFound" />
      </h3>
      <p className={styles.emptyStateDescription}>
        <Message id="metrics.searchEmptyState.trySearchingIn" />{' '}
        <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
          {msg('metrics.searchEmptyState.allMetrics')}
        </TextButton>{' '}
        or{' '}
        <TextButton className={styles.textButton} onPress={() => onCategoryChange('metric-groups')}>
          {msg('metrics.searchEmptyState.metricGroups')}
        </TextButton>
      </p>
    </div>
  );
}
